import React, { FC, useCallback, useContext, useMemo } from 'react';

import { ControlInfo } from 'ia-react-core/~/interfaces/ControlInfo';
import { MessageMap } from 'ia-react-core/~/interfaces/MessageMap';
import { ThemeContext } from 'styled-components';
import { ValidatorMap } from 'ia-react-core/~/interfaces/ValidatorMap';
import useScopedSelector from '../../../hooks/useScopedSelector';
import { ReactComponent as WarningIcon } from '../../../icons/warning.svg';
import Spacing from '../../grid/Spacing';
import ErrorMessage, { ErrorMessageProps } from '../../message/ErrorMessage';

import useValidationMessages from '../../../hooks/#useValidationMessages';
import CautionMessage from '../../message/CautionMessage';
import WarningMessage from '../../message/WarningMessage';
import ControlAnchor from '../ControlAnchor';

export interface FormMessagesProps extends ErrorMessageProps {
  formName: string;
  warningTitle?: string;
  cautionTitle?: string;
  controls?: ControlInfo[];
  errors?: Record<string, boolean | string>;
  warnings?: Record<string, boolean | string>;
  cautions?: Record<string, boolean | string>;
  messages?: MessageMap;
  validators?: ValidatorMap<any>;
}

const FormMessagesDefaultProps: Partial<FormMessagesProps> = {
  messages: {},
  errors: {},
  warnings: {},
  cautions: {},
};

const FormMessages: FC<FormMessagesProps> = ({
  formName, controls, errors, warnings, cautions, messages, errorTitle, warningTitle, cautionTitle, validators, ...props
}) => {
  const theme = useContext(ThemeContext);

  const formErrors = useScopedSelector(`${formName}.clientErrors.formErrors`);
  const formWarnings = useScopedSelector(`${formName}.clientErrors.formWarnings`);
  const formCautions = useScopedSelector(`${formName}.clientErrors.formCautions`);

  const formErrorMessages = useMemo(() => (
    formErrors && Object.values(formErrors)
  ), [formErrors]);

  const formWarningMessages = useMemo(() => (
    formWarnings && Object.values(formWarnings)
  ), [formWarnings]);

  const formCautionMessages = useMemo(() => (
    formCautions && Object.values(formCautions)
  ), [formCautions]);

  const getHoistedMessages = useCallback((validationMessages: ReturnType<typeof useValidationMessages>) => (
    validationMessages.map(({ message, type }) => {
      const errorFormScope = type.split('__').slice(0, -2);
      const prefix = errorFormScope.join('__');
      const name = type.split('__').at(-2);
      const currentInfo = controls.find((control) => (
        control.name === name &&
        control.formScope.slice(1).join('__') === prefix
      ));

      const { hideHoistedAnchor } = currentInfo;

      const link = hideHoistedAnchor ? null : (
        <><ControlAnchor name={name} formScope={errorFormScope} />: </>
      );

      return (
        <div>{link}{message}</div>
      );
    })
  ), [controls]);

  const errorValidationMessages = useValidationMessages({ errors, messages, validators, controls });
  const warningValidationMessages = useValidationMessages({ errors: warnings, messages, validators, controls });
  const cautionValidationMessages = useValidationMessages({ errors: cautions, messages, validators, controls });

  const controlsWithErrors = useMemo(() => (
    errorValidationMessages.reduce<Record<string, boolean>>((result, { type }) => {
      const name = type.split('__').slice(0, -1).join('__');
      if (errors[type]) {
        result[name] = true;
      }
      return result;
    }, {})
  ), [errorValidationMessages, errors]);

  const filteredWarningValidationMessages = useMemo(() => (
    warningValidationMessages.filter(({ type }) => {
      const name = type.split('__').slice(0, -1).join('__');
      return !controlsWithErrors[name];
    })
  ), [controlsWithErrors, warningValidationMessages]);

  const filteredCautionValidationMessages = useMemo(() => (
    cautionValidationMessages.filter(({ type }) => {
      const name = type.split('__').slice(0, -1).join('__');
      return !controlsWithErrors[name];
    })
  ), [controlsWithErrors, cautionValidationMessages]);

  const hoistedErrors = useMemo(() => getHoistedMessages(errorValidationMessages), [errorValidationMessages, getHoistedMessages]);
  const hoistedWarnings = useMemo(() => getHoistedMessages(filteredWarningValidationMessages), [filteredWarningValidationMessages, getHoistedMessages]);
  const hoistedCautions = useMemo(() => getHoistedMessages(filteredCautionValidationMessages), [filteredCautionValidationMessages, getHoistedMessages]);

  const combinedErrors = useMemo(() => [...formErrorMessages || [], ...hoistedErrors], [formErrorMessages, hoistedErrors]);
  const combinedWarnings = useMemo(() => [...formWarningMessages || [], ...hoistedWarnings], [formWarningMessages, hoistedWarnings]);
  const combinedCautions = useMemo(() => [...formCautionMessages || [], ...hoistedCautions], [formCautionMessages, hoistedCautions]);

  return (
    <>
      {combinedErrors?.length ? (
        <Spacing m-b={16}>
          <ErrorMessage
            data-form-message=""
            error={combinedErrors}
            useBullets={Boolean(hoistedErrors.length)}
            {...props}
            errorTitle={errorTitle}
          />
        </Spacing>
      ) : null}
      {combinedWarnings?.length ? (
        <Spacing m-b={16}>
          <ErrorMessage
            data-form-message=""
            error={combinedWarnings}
            useBullets={Boolean(hoistedWarnings.length)}
            as={WarningMessage}
            CustomIcon={WarningIcon}
            iconColor={theme.colors.warning}
            {...props}
            errorTitle={warningTitle}
          />
        </Spacing>
      ) : null}
      {combinedCautions?.length ? (
        <Spacing m-b={16}>
          <ErrorMessage
            data-form-message=""
            error={combinedCautions}
            useBullets={Boolean(hoistedCautions.length)}
            as={CautionMessage}
            CustomIcon={WarningIcon}
            iconColor={theme.colors.caution}
            {...props}
            errorTitle={cautionTitle}
          />
        </Spacing>
      ) : null}
    </>
  );
};

FormMessages.defaultProps = FormMessagesDefaultProps;

export default FormMessages;
